'use client';

import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useStickyHeaderOffset } from '@fshr/hooks';

import Image from '../image';
import Diamond from '../icon/diamond';
import PlusThin from '../icon/plus-thin';
import BevSafeText from '../bev-safe-text';
import type { ItineraryStepData } from './types';

export type Props = {
	steps: ItineraryStepData[];
	title?: string;
	className?: string;
};

const MIN_STEPS = 3;
const STEP_TRANSITION_MS = 200;
const SCROLL_TRANSITION_MS = 300;
// Pure anti-infinite-spin safety net for the position-preservation loop. It must
// sit well clear of a realistic worst case (delayed first paint + rAF throttling
// on a loaded CI runner) so the loop is ended by the collapse actually finishing,
// never by this cap firing mid-collapse.
const SCROLL_GUARD_TIMEOUT_MS = 5000;
// Consecutive on-target frames before the loop treats the collapse as finished.
const SCROLL_GUARD_SETTLED_FRAMES = 3;
// The collapsing panel's height is driven to 0 via a `0fr` grid row; treat any
// sub-pixel residue as fully collapsed.
const PANEL_COLLAPSED_MAX_HEIGHT_PX = 1;

export default function FullBleedItinerary({
	steps,
	title = 'Itinerary',
	className,
}: Props): ReactElement | null {
	const [activeIndex, setActiveIndex] = useState(0);
	const stickyOffset = useStickyHeaderOffset();
	const scrollAnchor = useRef<{
		button: HTMLButtonElement;
		top: number;
		initialTop: number;
		collapsingPanel: HTMLElement | null;
	} | null>(null);

	useLayoutEffect(() => {
		const anchor = scrollAnchor.current;
		if (!anchor) return;

		// Align the clicked heading below navigation while the preceding panel loses height.
		// Measure the actual position so native scroll anchoring is not applied twice.
		let frame = 0;
		let settledFrames = 0;
		const start = performance.now();
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const preservePosition = () => {
			const elapsed = performance.now() - start;
			// Eased scroll catch-up toward the sticky offset; reduced motion snaps there.
			const progress = reducedMotion ? 1 : Math.min(elapsed / SCROLL_TRANSITION_MS, 1);
			const easedProgress = progress * progress * (3 - 2 * progress);
			const targetTop = anchor.initialTop + (anchor.top - anchor.initialTop) * easedProgress;
			const delta = anchor.button.getBoundingClientRect().top - targetTop;
			if (Math.abs(delta) > 0.5) {
				window.scrollBy({ top: delta, behavior: 'instant' });
				settledFrames = 0;
			} else {
				settledFrames += 1;
			}
			// Keep guarding until the eased catch-up has finished, the collapsing panel
			// above has actually reached zero height, *and* the heading has held on
			// target for a few frames. A fixed time budget isn't safe on its own: the
			// grid transition's clock only starts at the first paint after the click,
			// which a loaded CI runner can delay well past a tight budget, leaving the
			// guard to stop mid-collapse and the heading a few px high.
			const panelCollapsed =
				!anchor.collapsingPanel ||
				anchor.collapsingPanel.getBoundingClientRect().height <= PANEL_COLLAPSED_MAX_HEIGHT_PX;
			const done =
				elapsed >= STEP_TRANSITION_MS &&
				progress >= 1 &&
				panelCollapsed &&
				settledFrames >= SCROLL_GUARD_SETTLED_FRAMES;
			if (!done && elapsed < SCROLL_GUARD_TIMEOUT_MS) {
				frame = requestAnimationFrame(preservePosition);
			}
		};
		preservePosition();
		return () => cancelAnimationFrame(frame);
	}, [activeIndex]);

	if (steps.length < MIN_STEPS) {
		return null;
	}

	return (
		<section className={cn(className)}>
			<div className="w-[100cqw] ml-[calc(50%-50cqw)] mr-[calc(50%-50cqw)] lg:grid lg:grid-cols-2 lg:items-start">
				<figure
					className="relative hidden overflow-hidden lg:block lg:sticky"
					style={{ top: stickyOffset }}
				>
					<div className="pt-[56.25%] lg:pt-[125%]" />
					{steps.map((step, index) => (
						<Image
							key={index}
							data-testid="itinerary-desktop-image"
							roughSize="l"
							imagePrimitive={step.image}
							className={cn(
								'absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
								index === activeIndex ? 'opacity-100' : 'opacity-0'
							)}
						/>
					))}
				</figure>
				<div data-testid="itinerary-content-column" className="min-w-0 lg:pt-700">
					{/* Match the full-page grid's right edge using the same container width as
					    the full-bleed wrapper, while keeping the image-side inset unchanged. */}
					<div
						className={cn(
							// Title-to-list gap: Figma "Itinerary Accordion" itemSpacing is
							// bound to stack-2xl (40px mobile / 70px desktop).
							'spacing-wide-grid flex flex-col gap-y-stack-2xl',
							'lg:pr-[max(calc(var(--spacing-300)*3),calc((100cqw_-_var(--wide-grid-container-width-lg))/2))]!',
							'xl:pr-[max(calc(var(--spacing-600)*2),calc((100cqw_-_var(--wide-grid-container-width-xl))/2))]!'
						)}
					>
						<h2 className="text-display3 capitalize">
							<BevSafeText>{title}</BevSafeText>
						</h2>
						<ol className="flex flex-col">
							{steps.map((step, index) => {
								const isActive = index === activeIndex;
								const contentId = `full-bleed-itinerary-step-${index}`;
								const isFirst = index === 0;

								return (
									<li key={index} className="flex gap-x-inline-xs">
										<div aria-hidden className="flex shrink-0 flex-col items-center">
											<div className={cn('h-300 w-px', !isFirst && 'bg-border-subtle')} />
											<div className="h-[11px] w-px bg-border-subtle" />
											<span className="relative shrink-0 text-text-primary [&_path]:fill-current">
												<Diamond width={12} height={12} />
											</span>
											<div className="w-px flex-1 bg-border-subtle" />
										</div>
										{/* Header-row-to-description gap: Figma's "Frame 14751"
										    itemSpacing is bound to stack-m in both frames. Lives on the
										    persistent wrapper, not inside the collapsing grid row, since it
										    still reads correctly against the existing collapse/scroll tests. */}
										<div className="flex min-w-0 flex-1 flex-col gap-y-stack-m">
											<h3 className="contents">
												<button
													type="button"
													onClick={event => {
														if (index === activeIndex) return;
														const button = event.currentTarget;
														scrollAnchor.current = {
															button,
															top: stickyOffset,
															initialTop: button.getBoundingClientRect().top,
															// The panel that is about to collapse is the one still active
															// on this click; the guard loop watches it shrink to zero.
															collapsingPanel: button.ownerDocument.getElementById(
																`full-bleed-itinerary-step-${activeIndex}`
															),
														};
														setActiveIndex(index);
													}}
													aria-expanded={isActive}
													aria-controls={contentId}
													// Badge/title/icon row gap: bound to inline-s in both frames
													// (stacked on mobile, side by side from lg) - bare form since the
													// axis flips at lg.
													className="flex w-full flex-col gap-inline-s py-300 text-left lg:flex-row lg:items-start"
												>
													<time
														className={cn(
															'spacing-tag-padding-x spacing-tag-padding-y w-fit rounded border border-border-subtle text-text-secondary text-center',
															isActive && 'bg-warm-stone/30'
														)}
													>
														<span className="text-subtitle2">{step.time}</span>
													</time>
													{/* Title-to-icon gap: flat 15px, only ever renders below lg
													    (lg:contents removes this div from layout above that, folding
													    title/icon into the row above governed by gap-inline-s instead)
													    - only one Figma data point (mobile), no second tier to make a
													    named token meaningful. */}
													<div className="flex items-start gap-150 lg:contents">
														<span className="text-h3 text-text-heading flex-1">
															<BevSafeText>{step.title}</BevSafeText>
														</span>
														{!isActive && (
															<span
																aria-hidden
																className="shrink-0 self-start mt-040 text-text-heading [&_path]:fill-current lg:mt-0 lg:self-center"
															>
																<PlusThin width={18} height={18} className="lg:hidden" />
																<PlusThin width={24} height={24} className="hidden lg:block" />
															</span>
														)}
													</div>
												</button>
											</h3>
											<div
												id={contentId}
												aria-hidden={!isActive}
												className="grid transition-[grid-template-rows] ease-in-out"
												style={{
													gridTemplateRows: isActive ? '1fr' : '0fr',
													transitionDuration: `${STEP_TRANSITION_MS}ms`,
												}}
											>
												<div className="overflow-hidden">
													<div className="flex flex-col gap-y-stack-s pb-card-padding-s">
														<div
															data-testid="itinerary-step-copy"
															className="flex flex-col gap-200"
														>
															<p className="text-body2 text-text-primary">
																<BevSafeText>{step.shortDescription}</BevSafeText>
															</p>
															{step.details && (
																<div
																	className="text-body2 text-text-primary"
																	dangerouslySetInnerHTML={{ __html: step.details }}
																/>
															)}
														</div>
														<figure
															className={cn(
																'relative w-full overflow-hidden lg:hidden',
																!isActive && 'invisible'
															)}
														>
															<div className="pt-[56.25%]" />
															<Image
																data-testid="itinerary-mobile-image"
																roughSize="m"
																imagePrimitive={step.image}
																className="absolute inset-0 h-full w-full object-cover"
															/>
														</figure>
													</div>
												</div>
											</div>
										</div>
									</li>
								);
							})}
						</ol>
					</div>
				</div>
			</div>
		</section>
	);
}
